import { useRef } from "react";
import { Camera, X, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { TextButton, CallButton } from "../site/Blocks";

const MAX = 10 * 1024 * 1024;
const MAX_COUNT = 10;

export function StepPhotos({ photos, setPhotos }) {
  const inputRef = useRef(null);
  const update = (key, patch) => setPhotos((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  const upload = async (p) => {
    const fd = new FormData();
    fd.append("file", p.file);
    try {
      const r = await api.post("/uploads", fd, { onUploadProgress: (ev) => ev.total && update(p.key, { progress: Math.round((ev.loaded / ev.total) * 100) }) });
      update(p.key, { status: "done", id: r.data.id, token: r.data.token, progress: 100 });
    } catch (e) {
      update(p.key, { status: "error", error: errMsg(e, "Upload failed. Try again or text the photo to us.") });
    }
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_COUNT - photos.length;
    const added = files.slice(0, Math.max(room, 0)).map((file) => {
      const key = `${Date.now()}-${Math.random()}`;
      const heic = /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
      let error = "";
      if (heic) error = "iPhone HEIC photos can't be uploaded here. Take a screenshot of the photo and upload that, or text the photo to us.";
      else if (!/^image\/(jpeg|png|webp)$/.test(file.type)) error = "Please choose a JPG, PNG or WebP photo, or text it to us.";
      else if (file.size > MAX) error = "This photo is larger than 10 MB. Choose a smaller one or text it to us.";
      return { key, file, name: file.name, preview: error ? null : URL.createObjectURL(file), status: error ? "error" : "uploading", error, progress: 0 };
    });
    setPhotos((ps) => [...ps, ...added]);
    added.filter((p) => p.status === "uploading").forEach(upload);
  };

  const remove = (key) => setPhotos((ps) => ps.filter((p) => p.key !== key));

  return (
    <div data-testid="step-photos">
      <h2 className="text-2xl font-bold">Photos <span className="text-base font-normal text-neutral-600">(optional)</span></h2>
      <p className="mt-2 text-lg text-neutral-700">Photos help us estimate. You can also call us for assistance.</p>
      <ul className="mt-3 list-disc pl-6 text-base text-neutral-700">
        <li>A wide shot of the whole area</li><li>Close-ups of specific items</li><li>The path from where a truck can park to the items</li>
      </ul>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={photos.length >= MAX_COUNT} className="mt-5 flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-neutral-400 bg-[#F5F5F5] p-8 text-lg font-bold hover:border-[#D9232E] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#D9232E]/40 disabled:opacity-50" data-testid="photo-add-btn">
        <Camera className="h-10 w-10 text-[#D9232E]" aria-hidden="true" />
        Add photos
        <span className="text-sm font-normal text-neutral-600">JPG, PNG or WebP · up to 10 MB each · up to {MAX_COUNT} photos</span>
      </button>
      <input ref={inputRef} type="file" accept="image/*" multiple className="sr-only" onChange={onPick} aria-label="Choose photos" data-testid="photo-file-input" />
      {photos.length > 0 && (
        <ul className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3" aria-live="polite">
          {photos.map((p) => (
            <li key={p.key} className="relative overflow-hidden rounded-md border border-neutral-300 bg-white" data-testid="photo-preview">
              {p.preview ? <img src={p.preview} alt={p.name} className="aspect-square w-full object-cover" /> : <div className="flex aspect-square items-center justify-center bg-neutral-100 p-2 text-center text-sm">{p.name}</div>}
              <div className="p-2 text-sm">
                {p.status === "uploading" && <span className="flex items-center gap-1"><Loader2 className="h-4 w-4 animate-spin" />Uploading {p.progress}%</span>}
                {p.status === "done" && <span className="flex items-center gap-1 font-bold text-green-800"><CheckCircle2 className="h-4 w-4" />Uploaded</span>}
                {p.status === "error" && <span className="flex gap-1 font-bold text-[#B81B25]" role="alert"><AlertCircle className="h-4 w-4 shrink-0" />{p.error}</span>}
                {p.status === "uploading" && <div className="mt-1 h-2 rounded bg-neutral-200"><div className="h-2 rounded bg-[#D9232E]" style={{ width: `${p.progress}%` }} /></div>}
              </div>
              <button type="button" onClick={() => remove(p.key)} className="absolute right-1 top-1 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#171717] text-white" aria-label={`Remove ${p.name}`} data-testid="photo-remove-btn"><X className="h-5 w-5" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-6 rounded-md bg-[#F5F5F5] p-4 text-base">
        <p className="font-bold">Photos not working, or prefer not to upload?</p>
        <p className="mt-1 text-neutral-700">Skip this step, text photos to us, or call — you'll still get an estimate.</p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row"><TextButton className="btn-outline btn-sm" testid="photos-text-btn" /><CallButton className="btn-outline btn-sm" label="Call instead" testid="photos-call-btn" /></div>
      </div>
    </div>
  );
}
